import {
  View,
  Text,
  Pressable,
  ScrollView,
  ActivityIndicator,
  Linking as RNLinking,
  TextInput,
  Platform,
} from 'react-native';
import { useState, useCallback, useEffect, useRef } from 'react';
import { Ionicons } from '@expo/vector-icons';
import { supabase } from '../../lib/supabase';
import { useIsFocused, useLocalSearchParams, useRouter } from 'expo-router';
import { trpc } from '../../lib/trpc';
import {
  BrandSwitch,
  Card,
  SectionHeader,
  Button,
  PageContainer,
  PageHeader,
  SubscriptionPaywall,
  WebHead,
} from '../../components';
import type { SubscriptionPaywallTrigger } from '../../components/SubscriptionPaywall';
import { useTheme, type ThemePreference } from '../../lib/theme';
import { useColors } from '../../lib/colors';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import { logOutRevenueCat } from '../../lib/purchases';
import { getLegalUrl, openExternalUrl } from '../../lib/utils';
import { parseNonNegativeCurrencyInput } from '../../lib/profile-fields';

const PRIVACY_POLICY_URL = getLegalUrl('privacy');
const TERMS_OF_SERVICE_URL = getLegalUrl('terms');
const IS_WEB = Platform.OS === 'web';
const SETTINGS_OFFERING_IDENTIFIER = 'default';

function getSubscriptionCancellationWarning(platform: string): string | null {
  switch (platform) {
    case 'ios':
      return 'Deleting your Per Diem account does not cancel your App Store subscription. Cancel it separately in Apple subscription settings to avoid future charges.';
    case 'android':
      return 'Deleting your Per Diem account does not cancel your Google Play subscription. Cancel it separately in Play Store subscription settings to avoid future charges.';
    default:
      return null;
  }
}

function SettingsRow({
  label,
  value,
  onPress,
}: {
  label: string;
  value?: string | null;
  onPress?: () => void;
}) {
  return (
    <Pressable
      onPress={onPress}
      className="flex-row items-center justify-between py-3 border-b border-gray-50 dark:border-gray-700"
    >
      <Text className="text-sm text-gray-600 dark:text-gray-400">{label}</Text>
      <View className="flex-row items-center gap-2">
        {value && <Text className="text-sm text-gray-800 dark:text-gray-200">{value}</Text>}
        {onPress && <Text className="text-gray-300 dark:text-gray-600">›</Text>}
      </View>
    </Pressable>
  );
}

function NotificationToggleRow({
  label,
  description,
  value,
  onValueChange,
}: {
  label: string;
  description?: string;
  value: boolean;
  onValueChange: (value: boolean) => void;
}) {
  return (
    <View className="flex-row items-start justify-between gap-3 py-2 border-b border-gray-50 dark:border-gray-700">
      <View className="flex-1">
        <Text className="text-sm font-medium text-gray-900 dark:text-gray-100">{label}</Text>
        {description ? (
          <Text className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{description}</Text>
        ) : null}
      </View>
      <BrandSwitch value={value} onValueChange={onValueChange} accessibilityLabel={label} />
    </View>
  );
}

export default function SettingsScreen() {
  const isDesktop = useIsDesktop();
  const isFocused = useIsFocused();
  const { preference: themePreference, setPreference: setThemePreference } = useTheme();
  const c = useColors();
  const router = useRouter();
  const utils = trpc.useUtils();
  const householdQ = trpc.households.get.useQuery(undefined, { enabled: isFocused });
  const profilesQ = trpc.profiles.list.useQuery(undefined, { enabled: isFocused });
  const subscriptionQ = trpc.subscription.getStatus.useQuery(undefined, { enabled: isFocused });
  const refreshSubscriptionStatusMutation = trpc.subscription.refreshStatus.useMutation({
    onSuccess: (data) => {
      utils.subscription.getStatus.setData(undefined, data);
      void utils.bootstrap.get.invalidate();
    },
  });
  const createManagementUrlMutation = trpc.subscription.createManagementUrl.useMutation();
  const deleteHouseholdMutation = trpc.households.delete.useMutation();

  const notificationPrefsQ = trpc.households.getNotificationPreferences.useQuery(undefined, {
    enabled: isFocused,
  });
  const updateNotificationPrefsMutation = trpc.households.updateNotificationPreferences.useMutation({
    onMutate: async (input) => {
      await utils.households.getNotificationPreferences.cancel();
      const previous = utils.households.getNotificationPreferences.getData();
      if (previous) {
        utils.households.getNotificationPreferences.setData(undefined, { ...previous, ...input });
      }
      return { previous };
    },
    onError: (_err, _input, ctx) => {
      if (ctx?.previous) {
        utils.households.getNotificationPreferences.setData(undefined, ctx.previous);
      }
    },
    onSettled: () => {
      utils.households.getNotificationPreferences.invalidate();
    },
  });

  const emailPrefsQ = trpc.profiles.getEmailPreferences.useQuery(undefined, {
    enabled: isFocused,
  });
  const updateEmailPrefsMutation = trpc.profiles.updateEmailPreferences.useMutation({
    onMutate: async (input) => {
      await utils.profiles.getEmailPreferences.cancel();
      const previous = utils.profiles.getEmailPreferences.getData();
      if (previous) {
        utils.profiles.getEmailPreferences.setData(undefined, { ...previous, ...input });
      }
      return { previous };
    },
    onError: (_err, _input, ctx) => {
      if (ctx?.previous) {
        utils.profiles.getEmailPreferences.setData(undefined, ctx.previous);
      }
    },
    onSettled: () => {
      utils.profiles.getEmailPreferences.invalidate();
    },
  });

  const profiles = profilesQ.data ?? [];
  const currentProfile = profiles.find((profile) => profile.isSelf) ?? profiles[0];
  const isPrimaryUser = currentProfile?.type === 'primary';
  const subscription = subscriptionQ.data;
  const isDemo = householdQ.data?.isDemo ?? false;
  const isAccessRestricted = Boolean(
    !isDemo
      && subscription
      && !subscription.isActive,
  );
  const isPromoTrial =
    subscription?.accessKind === 'trial_promo'
    || subscription?.accessKind === 'trial_grace'
    || subscription?.accessKind === 'expired_trial_promo';
  const isTrialGrace = subscription?.accessKind === 'trial_grace';
  const isManualEntitlement =
    subscription?.accessKind === 'manual_entitlement'
    || subscription?.accessKind === 'expired_manual_entitlement';
  const isSubscriptionCanceled = Boolean(
    subscription?.accessKind === 'subscription'
      && subscription.subscriptionIsCanceled,
  );
  const hasEndedSubscriptionDuringPromo = Boolean(
    isPromoTrial
      && subscription?.subscriptionProviderSubscriptionId
      && subscription.subscriptionIsCanceled,
  );
  const subscriptionDisplayExpiresAt =
    isSubscriptionCanceled
      ? subscription?.expiresAt ?? subscription?.subscriptionCurrentPeriodEndsAt ?? null
      : subscription?.expiresAt ?? null;
  const subscriptionAccessHasEnded = Boolean(
    isSubscriptionCanceled
      && subscriptionDisplayExpiresAt
      && new Date(subscriptionDisplayExpiresAt) <= new Date(),
  );
  const subscriptionCancellationWarning = subscription?.isActive && subscription.accessKind === 'subscription'
    ? getSubscriptionCancellationWarning(Platform.OS)
    : null;
  const subscriptionPlanLabel = isDemo
    ? 'Demo access'
    : isPromoTrial
      ? subscription?.isActive
        ? 'Promotional trial'
        : 'Expired promotional trial'
      : isManualEntitlement
        ? subscription?.isActive
          ? 'Complimentary access'
          : 'Expired complimentary access'
      : isSubscriptionCanceled
        ? 'Canceled subscription'
      : subscription?.isActive
        ? 'Per Diem subscription'
        : 'No subscription';
  const subscriptionDateLabel = isPromoTrial
    ? subscription?.isActive
      ? 'Trial ends'
      : 'Trial ended'
    : isManualEntitlement
      ? subscription?.isActive
        ? 'Access ends'
        : 'Access ended'
    : isSubscriptionCanceled
      ? subscriptionAccessHasEnded
        ? 'Access ended'
        : 'Access through'
    : subscription?.isActive
      ? 'Renews'
      : 'Ended';
  const canceledSubscriptionMessage =
    isSubscriptionCanceled && subscriptionDisplayExpiresAt
      ? `Your subscription is canceled and will not renew. You can keep using Per Diem until ${new Date(subscriptionDisplayExpiresAt).toLocaleDateString()}.`
      : isSubscriptionCanceled
        ? 'Your subscription is canceled and will not renew.'
        : null;
  const promoPaidSubscriptionStatusText = subscription?.subscriptionIsExpired
    ? 'was canceled and ended'
    : 'is canceled and will end';
  const promoTrialMessage = isPromoTrial
    ? isTrialGrace
      ? 'Your promotional trial has ended and access is paused. Your accounts, transactions, and FIRE plan are saved — subscribe to pick up right where you left off.'
    : subscription?.isActive && hasEndedSubscriptionDuringPromo
      ? `Your paid subscription ${promoPaidSubscriptionStatusText}${subscription?.subscriptionCurrentPeriodEndsAt ? ` on ${new Date(subscription.subscriptionCurrentPeriodEndsAt).toLocaleDateString()}` : ''}. Your promotional trial remains active until ${subscription.expiresAt ? new Date(subscription.expiresAt).toLocaleDateString() : 'its expiration date'}.`
      : subscription?.isActive
        ? 'Your promotional trial does not become a paid plan on its own. Subscribe before it ends to keep access to Per Diem.'
      : 'Your promotional trial has ended. Subscribe to restore access to Per Diem.'
    : null;
  const manualEntitlementMessage = isManualEntitlement
    ? subscription?.isActive
      ? 'This account has complimentary access, so there is no billing subscription to manage.'
      : 'This complimentary access has ended. Subscribe to restore access to Per Diem.'
    : null;
  const paywallDescription = hasEndedSubscriptionDuringPromo
    ? 'Choose a subscription to keep Per Diem active after your promotional trial. Billing is handled by RevenueCat.'
    : isTrialGrace
      ? 'Your promotional trial has ended. Everything you set up is saved — subscribing turns it all back on instantly.'
    : isPromoTrial
      ? 'Your promotional trial does not convert to a paid plan on its own. Choose a subscription to keep Per Diem active after the trial.'
    : IS_WEB
      ? 'Choose a subscription to keep using Per Diem.'
      : 'Replace your budgeting app, investment tracker, and retirement calculator. One app, one subscription.';

  // Delete account confirmation state: null | 'confirm1' | 'confirm2'
  const [deleteStage, setDeleteStage] = useState<null | 'confirm1' | 'confirm2'>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Subscription paywall shell state — purchase logic lives in SubscriptionPaywall.
  const [showPaywall, setShowPaywall] = useState(false);
  const [paywallTrigger, setPaywallTrigger] = useState<SubscriptionPaywallTrigger>('settings');
  const [manageSubscriptionError, setManageSubscriptionError] = useState<string | null>(null);
  const [manageSubscriptionMessage, setManageSubscriptionMessage] = useState<string | null>(null);
  const [subscriptionPortalRefreshPending, setSubscriptionPortalRefreshPending] = useState(false);
  const subscriptionRefreshInFlightRef = useRef(false);
  const refreshedSubscriptionOnFocusRef = useRef(false);

  // Notification threshold drafts (synced from server query)
  const [purchaseThresholdDraft, setPurchaseThresholdDraft] = useState<string>('');
  const [tradeThresholdDraft, setTradeThresholdDraft] = useState<string>('');
  const notificationPrefs = notificationPrefsQ.data;
  useEffect(() => {
    if (notificationPrefs?.largePurchaseThreshold != null) {
      setPurchaseThresholdDraft(String(notificationPrefs.largePurchaseThreshold));
    }
    if (notificationPrefs?.largeTradeThreshold != null) {
      setTradeThresholdDraft(String(notificationPrefs.largeTradeThreshold));
    }
  }, [
    notificationPrefs?.largePurchaseThreshold,
    notificationPrefs?.largeTradeThreshold,
  ]);

  const handleDeleteAccount = useCallback(async () => {
    setDeleteError(null);
    try {
      await deleteHouseholdMutation.mutateAsync();
      await logOutRevenueCat();
      await supabase.auth.signOut();
      router.replace('/(auth)/login');
    } catch (e: unknown) {
      setDeleteError(e instanceof Error ? e.message : 'Failed to delete account. Please try again.');
      setDeleteStage(null);
    }
  }, [deleteHouseholdMutation, router]);

  const openPaywall = (trigger: SubscriptionPaywallTrigger = 'settings') => {
    setPaywallTrigger(trigger);
    setShowPaywall(true);
  };

  // Open the paywall immediately instead of waiting for a tap when either
  // (a) an expired household lands here via the app-shell redirect, or
  // (b) the visit came from a subscribe deep link (email CTAs use ?subscribe=1).
  // Once per mount so closing it doesn't re-trigger.
  const { subscribe: subscribeParam } = useLocalSearchParams<{ subscribe?: string }>();
  const autoOpenedPaywallRef = useRef(false);
  const paywallOpenedForRestrictedAccessRef = useRef(false);
  useEffect(() => {
    if (autoOpenedPaywallRef.current || !isPrimaryUser || isDemo) return;
    if (!isAccessRestricted && subscribeParam !== '1') return;
    autoOpenedPaywallRef.current = true;
    paywallOpenedForRestrictedAccessRef.current = isAccessRestricted;
    openPaywall(isAccessRestricted ? 'restricted_access' : 'subscribe_deep_link');
  });

  // Restricted users never chose to visit Settings — the app shell forced them
  // here. After a successful activation, send them on to the dashboard instead
  // of leaving them on Settings. Voluntary paywall visits stay put.
  const closePaywallAfterActivation = () => {
    setShowPaywall(false);
    if (paywallOpenedForRestrictedAccessRef.current) {
      paywallOpenedForRestrictedAccessRef.current = false;
      router.replace('/(app)');
    }
  };

  const refreshSubscriptionStatus = useCallback(async (options?: {
    automatic?: boolean;
    showError?: boolean;
    showMessage?: boolean;
  }) => {
    if (subscriptionRefreshInFlightRef.current) return;

    subscriptionRefreshInFlightRef.current = true;
    if (options?.showError !== false) {
      setManageSubscriptionError(null);
    }

    try {
      await refreshSubscriptionStatusMutation.mutateAsync();
      await utils.subscription.getStatus.invalidate();
      setSubscriptionPortalRefreshPending(false);
      if (options?.showMessage !== false) {
        setManageSubscriptionMessage(
          options?.automatic
            ? 'Subscription status refreshed after your RevenueCat portal visit.'
            : 'Subscription status refreshed.',
        );
      }
    } catch {
      if (options?.showError !== false) {
        setManageSubscriptionError('Could not refresh your subscription status. Please try again.');
      }
    } finally {
      subscriptionRefreshInFlightRef.current = false;
    }
  }, [refreshSubscriptionStatusMutation, utils.subscription.getStatus]);

  useEffect(() => {
    if (!isFocused) {
      refreshedSubscriptionOnFocusRef.current = false;
      return;
    }

    if (
      isDemo
      || !subscription?.isActive
      || (
        subscription.accessKind !== 'subscription'
        && !subscription.subscriptionProviderSubscriptionId
      )
      || refreshedSubscriptionOnFocusRef.current
    ) {
      return;
    }

    refreshedSubscriptionOnFocusRef.current = true;
    void refreshSubscriptionStatus({
      automatic: true,
      showError: false,
      showMessage: false,
    });
  }, [
    isDemo,
    isFocused,
    refreshSubscriptionStatus,
    subscription?.accessKind,
    subscription?.isActive,
  ]);

  useEffect(() => {
    if (
      !IS_WEB
      || !subscriptionPortalRefreshPending
      || typeof window === 'undefined'
      || typeof document === 'undefined'
    ) {
      return;
    }

    const refreshWhenVisible = () => {
      if (document.visibilityState !== 'visible') return;
      void refreshSubscriptionStatus({ automatic: true });
    };

    window.addEventListener('focus', refreshWhenVisible);
    document.addEventListener('visibilitychange', refreshWhenVisible);

    return () => {
      window.removeEventListener('focus', refreshWhenVisible);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [refreshSubscriptionStatus, subscriptionPortalRefreshPending]);

  const handleManageSubscription = async () => {
    setManageSubscriptionError(null);
    setManageSubscriptionMessage(null);

    if (Platform.OS === 'web') {
      // Store-billed subscriptions have no RevenueCat web portal — send the
      // user to the right store's subscription page instead of erroring.
      const store = subscription?.subscriptionStore;
      if (store === 'play_store' || store === 'app_store' || store === 'mac_app_store') {
        const isPlayStore = store === 'play_store';
        openExternalUrl(
          isPlayStore
            ? 'https://play.google.com/store/account/subscriptions'
            : 'https://apps.apple.com/account/subscriptions',
        );
        setSubscriptionPortalRefreshPending(true);
        setManageSubscriptionMessage(
          isPlayStore
            ? 'This subscription is billed through Google Play. We opened your Google Play subscriptions — you can also manage it from the Play Store app on your Android device.'
            : 'This subscription is billed through the App Store. We opened your Apple subscriptions — you can also manage it from Settings on your iPhone or iPad.',
        );
        return;
      }

      try {
        const { managementUrl } = await createManagementUrlMutation.mutateAsync();
        openExternalUrl(managementUrl);
        setSubscriptionPortalRefreshPending(true);
        setManageSubscriptionMessage(
          'RevenueCat opened in a new tab. When you return here, Per Diem will refresh your subscription status.',
        );
        return;
      } catch (error) {
        const message = error instanceof Error ? error.message : '';
        setManageSubscriptionError(
          message.includes('No billing subscription')
            ? 'There is no billing subscription to manage for this account.'
            : 'Could not open the subscription portal. Please try again in a moment.',
        );
      }

      return;
    }

    if (Platform.OS === 'ios') {
      RNLinking.openURL('https://apps.apple.com/account/subscriptions');
    } else if (Platform.OS === 'android') {
      RNLinking.openURL('https://play.google.com/store/account/subscriptions');
    }
  };

  return (
    <>
      <WebHead><title>Settings – Per Diem</title></WebHead>
      <ScrollView className="flex-1 bg-canvas dark:bg-canvas-dark">
        <PageContainer className={isDesktop ? '' : 'p-4'} size="narrow">
        <PageHeader title="Settings" closeTo="/(app)" />
        <View className="gap-4">
        {isDemo && (
          <Card>
            <Text className="text-sm font-semibold text-amber-900">Read-only demo account</Text>
            <Text className="mt-1 text-sm text-amber-800">
              Account linking, profile edits, invitations, subscription changes, exports, and destructive actions are disabled in demos.
            </Text>
          </Card>
        )}

        {/* Subscription */}
        <Card>
          <SectionHeader title="Subscription" />
          <View className="flex-row items-center justify-between py-2">
            <Text className="text-sm text-gray-600 dark:text-gray-400">Plan</Text>
            <Text className="text-sm font-semibold text-gray-800 dark:text-gray-200">
              {subscriptionPlanLabel}
            </Text>
          </View>
          {subscriptionDisplayExpiresAt && (
            <View className="flex-row items-center justify-between py-2">
              <Text className="text-sm text-gray-600 dark:text-gray-400">{subscriptionDateLabel}</Text>
              <Text className="text-sm text-gray-500">
                {new Date(subscriptionDisplayExpiresAt).toLocaleDateString()}
              </Text>
            </View>
          )}
          {isDemo ? (
            <Text className="mt-2 text-xs text-gray-500">
              Demo access includes a seeded premium household. Subscription changes are disabled.
            </Text>
          ) : isPromoTrial ? (
            isPrimaryUser ? (
              <>
                {promoTrialMessage && (
                  <Text className="mt-2 text-xs text-gray-500">
                    {promoTrialMessage}
                  </Text>
                )}
                {hasEndedSubscriptionDuringPromo ? (
                  <>
                    <View className="mt-2 gap-2">
                      <Button size="sm" onPress={() => openPaywall()}>
                        Choose a subscription
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        onPress={handleManageSubscription}
                        loading={createManagementUrlMutation.isPending}
                      >
                        View billing history
                      </Button>
                    </View>
                    {manageSubscriptionMessage && (
                      <View className="mt-2 rounded-xl bg-green-50 px-4 py-3 dark:bg-green-950">
                        <Text className="text-xs text-green-800 dark:text-green-100">
                          {manageSubscriptionMessage}
                        </Text>
                        {subscriptionPortalRefreshPending && (
                          <Button
                            size="sm"
                            variant="secondary"
                            className="mt-3 self-start"
                            onPress={() => void refreshSubscriptionStatus()}
                            loading={refreshSubscriptionStatusMutation.isPending}
                          >
                            Refresh status
                          </Button>
                        )}
                      </View>
                    )}
                    {manageSubscriptionError && (
                      <Text className="mt-2 text-xs text-red-600">{manageSubscriptionError}</Text>
                    )}
                  </>
                ) : (
                  <Button size="sm" className="mt-2" onPress={() => openPaywall()}>
                    {subscription?.isActive
                      ? 'Subscribe before trial ends'
                      : 'Subscribe to continue'}
                  </Button>
                )}
              </>
            ) : (
              <Text className="mt-2 text-xs text-gray-500">
                Subscription changes are managed by the primary household owner.
              </Text>
            )
          ) : isManualEntitlement ? (
            isPrimaryUser ? (
              <>
                {manualEntitlementMessage && (
                  <Text className="mt-2 text-xs text-gray-500">
                    {manualEntitlementMessage}
                  </Text>
                )}
                {!subscription?.isActive && (
                  <Button size="sm" className="mt-2" onPress={() => openPaywall()}>
                    Subscribe to Per Diem
                  </Button>
                )}
              </>
            ) : (
              <Text className="mt-2 text-xs text-gray-500">
                Subscription changes are managed by the primary household owner.
              </Text>
            )
          ) : subscription?.isActive ? (
            isPrimaryUser ? (
              <>
                {canceledSubscriptionMessage && (
                  <View className="mt-2 rounded-xl bg-amber-50 px-4 py-3 dark:bg-amber-950">
                    <Text className="text-xs font-medium text-amber-900 dark:text-amber-100">
                      {canceledSubscriptionMessage}
                    </Text>
                  </View>
                )}
                <Button
                  size="sm"
                  className="mt-2"
                  onPress={handleManageSubscription}
                  loading={createManagementUrlMutation.isPending}
                >
                  Manage Subscription
                </Button>
                {manageSubscriptionMessage && (
                  <View className="mt-2 rounded-xl bg-green-50 px-4 py-3 dark:bg-green-950">
                    <Text className="text-xs text-green-800 dark:text-green-100">
                      {manageSubscriptionMessage}
                    </Text>
                    {subscriptionPortalRefreshPending && (
                      <Button
                        size="sm"
                        variant="secondary"
                        className="mt-3 self-start"
                        onPress={() => void refreshSubscriptionStatus()}
                        loading={refreshSubscriptionStatusMutation.isPending}
                      >
                        Refresh status
                      </Button>
                    )}
                  </View>
                )}
                {manageSubscriptionError && (
                  <Text className="mt-2 text-xs text-red-600">{manageSubscriptionError}</Text>
                )}
              </>
            ) : (
              <Text className="mt-2 text-xs text-gray-500">
                Subscription changes are managed by the primary household owner.
              </Text>
            )
          ) : (
            isPrimaryUser ? (
              <Button size="sm" className="mt-2" onPress={() => openPaywall()}>
                Subscribe to Per Diem
              </Button>
            ) : (
              <Text className="mt-2 text-xs text-gray-500">
                Subscription changes are managed by the primary household owner.
              </Text>
            )
          )}
        </Card>

        {!isAccessRestricted && (
          <>
            {/* Notifications */}
            <Card>
              <SectionHeader title="Notifications" />
              {notificationPrefsQ.isLoading || emailPrefsQ.isLoading ? (
                <ActivityIndicator />
              ) : (
                <View className="gap-1">
                  <Text className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Push
                  </Text>
                  <NotificationToggleRow
                    label="Morning per diem"
                    description="Daily summary of how much you can spend today."
                    value={notificationPrefs?.morningPerdiem ?? true}
                    onValueChange={(v) =>
                      updateNotificationPrefsMutation.mutate({ morningPerdiem: v })
                    }
                  />
                  <NotificationToggleRow
                    label="Transactions to review"
                    description="Reminder when transactions need to be categorized."
                    value={notificationPrefs?.reviewTransactions ?? true}
                    onValueChange={(v) =>
                      updateNotificationPrefsMutation.mutate({ reviewTransactions: v })
                    }
                  />
                  <NotificationToggleRow
                    label="Large purchases"
                    description={
                      notificationPrefs?.largePurchase
                        ? `Alert when a purchase exceeds $${
                            notificationPrefs?.largePurchaseThreshold ?? 100
                          }.`
                        : 'Alerts disabled.'
                    }
                    value={notificationPrefs?.largePurchase ?? true}
                    onValueChange={(v) =>
                      updateNotificationPrefsMutation.mutate({ largePurchase: v })
                    }
                  />
                  {notificationPrefs?.largePurchase && (
                    <View className="ml-1 flex-row items-center gap-2 pl-1 pb-2">
                      <Text className="text-xs text-gray-500 dark:text-gray-400">Threshold ($)</Text>
                      <TextInput
                        value={purchaseThresholdDraft}
                        onChangeText={setPurchaseThresholdDraft}
                        keyboardType="numeric"
                        onBlur={() => {
                          const parsed = parseNonNegativeCurrencyInput(purchaseThresholdDraft);
                          if (parsed != null && parsed !== notificationPrefs?.largePurchaseThreshold) {
                            updateNotificationPrefsMutation.mutate({
                              largePurchaseThreshold: parsed,
                            });
                          }
                        }}
                        className="flex-1 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-1.5 text-sm text-gray-900 dark:text-gray-100"
                      />
                    </View>
                  )}
                  <NotificationToggleRow
                    label="Large trades"
                    description={
                      notificationPrefs?.largeTrade
                        ? `Alert when an investment trade exceeds $${
                            notificationPrefs?.largeTradeThreshold ?? 500
                          }.`
                        : 'Alerts disabled.'
                    }
                    value={notificationPrefs?.largeTrade ?? true}
                    onValueChange={(v) =>
                      updateNotificationPrefsMutation.mutate({ largeTrade: v })
                    }
                  />
                  {notificationPrefs?.largeTrade && (
                    <View className="ml-1 flex-row items-center gap-2 pl-1 pb-2">
                      <Text className="text-xs text-gray-500 dark:text-gray-400">Threshold ($)</Text>
                      <TextInput
                        value={tradeThresholdDraft}
                        onChangeText={setTradeThresholdDraft}
                        keyboardType="numeric"
                        onBlur={() => {
                          const parsed = parseNonNegativeCurrencyInput(tradeThresholdDraft);
                          if (parsed != null && parsed !== notificationPrefs?.largeTradeThreshold) {
                            updateNotificationPrefsMutation.mutate({
                              largeTradeThreshold: parsed,
                            });
                          }
                        }}
                        className="flex-1 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-1.5 text-sm text-gray-900 dark:text-gray-100"
                      />
                    </View>
                  )}
                  <NotificationToggleRow label="FI milestones" description="Celebrate when your household first meets its FI target." value={notificationPrefs?.fiMilestone ?? true} onValueChange={v => updateNotificationPrefsMutation.mutate({ fiMilestone: v })} />
                  <NotificationToggleRow label="Retirement plan health" description="Meaningful changes when your plan needs review or recovers." value={notificationPrefs?.fiHealth ?? true} onValueChange={v => updateNotificationPrefsMutation.mutate({ fiHealth: v })} />
                  <NotificationToggleRow
                    label="FI year changes"
                    description="Alert when a recalculation moves your FI year."
                    value={notificationPrefs?.fiYearChange ?? true}
                    onValueChange={(v) =>
                      updateNotificationPrefsMutation.mutate({ fiYearChange: v })
                    }
                  />

                  <Text className="mb-1 mt-3 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Email
                  </Text>
                  <NotificationToggleRow label="FI milestone email" description="A celebration sent to your email when your household first meets its target." value={emailPrefsQ.data?.fiMilestone ?? true} onValueChange={v => updateEmailPrefsMutation.mutate({ fiMilestone: v })} />
                  <NotificationToggleRow label="Retirement plan health email" description="Meaningful changes to your retirement outlook." value={emailPrefsQ.data?.fiHealth ?? true} onValueChange={v => updateEmailPrefsMutation.mutate({ fiHealth: v })} />
                  <NotificationToggleRow
                    label="Weekly spending report"
                    description="Sent every Monday morning, summarizing the prior week."
                    value={emailPrefsQ.data?.weeklyReport ?? true}
                    onValueChange={(v) =>
                      updateEmailPrefsMutation.mutate({ weeklyReport: v })
                    }
                  />
                  <NotificationToggleRow
                    label="90-day budget check-in"
                    description="Recurring budget reality check from recent spending."
                    value={emailPrefsQ.data?.budgetCheckIn ?? true}
                    onValueChange={(v) =>
                      updateEmailPrefsMutation.mutate({ budgetCheckIn: v })
                    }
                  />
                </View>
              )}
            </Card>

            {/* Theme */}
            <Card>
              <SectionHeader title="Theme" />
              <View className="flex-row gap-2">
                {([
                  { value: 'system' as ThemePreference, label: 'System' },
                  { value: 'light' as ThemePreference, label: 'Light' },
                  { value: 'dark' as ThemePreference, label: 'Dark' },
                ]).map((opt) => (
                  <Pressable
                    key={opt.value}
                    onPress={() => setThemePreference(opt.value)}
                    className={`flex-1 items-center py-2 rounded-lg border ${
                      themePreference === opt.value
                        ? 'border-green-600 bg-green-50 dark:bg-green-900/30'
                        : 'border-gray-200 dark:border-gray-600'
                    }`}
                  >
                    <Text
                      className={`text-sm font-medium ${
                        themePreference === opt.value
                          ? 'text-forest-700 dark:text-forest-500'
                          : 'text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      {opt.label}
                    </Text>
                  </Pressable>
                ))}
              </View>
            </Card>
          </>
        )}

        {/* Data & Privacy */}
        <Card>
          <SectionHeader title="Data & Privacy" />
          <SettingsRow label="Privacy policy" onPress={() => openExternalUrl(PRIVACY_POLICY_URL)} />
          <SettingsRow label="Terms of service" onPress={() => openExternalUrl(TERMS_OF_SERVICE_URL)} />
        </Card>

        {isPrimaryUser && !isDemo && (
          <Card>
            <Text className="text-sm font-semibold text-red-600 mb-3">Danger Zone</Text>

            {deleteError && (
              <View className="rounded-xl bg-red-50 px-4 py-3 mb-3">
                <Text className="text-sm text-red-600">{deleteError}</Text>
              </View>
            )}

            {deleteStage === null && (
              <Button variant="destructive" onPress={() => setDeleteStage('confirm1')} size="sm">
                Delete Account
              </Button>
            )}

            {deleteStage === 'confirm1' && (
              <View className="gap-3">
                <View className="rounded-xl bg-red-50 px-4 py-3">
                  <Text className="text-sm font-semibold text-red-700 mb-1">Are you sure?</Text>
                  <Text className="text-sm text-red-600">
                    This will permanently delete your account and all data. This cannot be undone.
                  </Text>
                </View>
                {subscriptionCancellationWarning && (
                  <View className="rounded-xl bg-amber-50 px-4 py-3">
                    <Text className="text-sm font-semibold text-amber-800 mb-1">Subscription notice</Text>
                    <Text className="text-sm text-amber-700">
                      {subscriptionCancellationWarning}
                    </Text>
                  </View>
                )}
                <View className="flex-row gap-2">
                  <Button variant="destructive" onPress={() => setDeleteStage('confirm2')} size="sm" className="flex-1">
                    Yes, delete my account
                  </Button>
                  <Button variant="secondary" onPress={() => setDeleteStage(null)} size="sm">
                    Cancel
                  </Button>
                </View>
              </View>
            )}

            {deleteStage === 'confirm2' && (
              <View className="gap-3">
                <View className="rounded-xl bg-red-50 px-4 py-3">
                  <Text className="text-sm font-semibold text-red-700 mb-1">Final confirmation</Text>
                  <Text className="text-sm text-red-600">
                    Your account, all financial data, and FIRE projections will be deleted immediately.
                  </Text>
                </View>
                {subscriptionCancellationWarning && (
                  <View className="rounded-xl bg-amber-50 px-4 py-3">
                    <Text className="text-sm font-semibold text-amber-800 mb-1">Subscription notice</Text>
                    <Text className="text-sm text-amber-700">
                      {subscriptionCancellationWarning}
                    </Text>
                    <Button
                      size="sm"
                      variant="secondary"
                      className="mt-3 self-start"
                      onPress={handleManageSubscription}
                      loading={createManagementUrlMutation.isPending}
                    >
                      Open Subscription Settings
                    </Button>
                    {manageSubscriptionError && (
                      <Text className="mt-2 text-xs text-red-600">{manageSubscriptionError}</Text>
                    )}
                  </View>
                )}
                <View className="flex-row gap-2">
                  <Button
                    variant="destructive"
                    onPress={handleDeleteAccount}
                    loading={deleteHouseholdMutation.isPending}
                    size="sm"
                    className="flex-1"
                  >
                    Delete forever
                  </Button>
                  <Button variant="secondary" onPress={() => setDeleteStage(null)} size="sm">
                    Cancel
                  </Button>
                </View>
              </View>
            )}
          </Card>
        )}

      </View>
      </PageContainer>
      </ScrollView>

      <SubscriptionPaywall
        visible={showPaywall}
        trigger={paywallTrigger}
        offeringIdentifier={SETTINGS_OFFERING_IDENTIFIER}
        onClose={() => setShowPaywall(false)}
        onActivated={closePaywallAfterActivation}
        opaqueBackdrop={isAccessRestricted}
        fallbackDescription={paywallDescription}
      />
    </>
  );
}
